import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Checkbox, Grid } from '@mui/material';
import { Box, FormControlLabel } from '@mui/material';
import { FieldType } from '../types';
import { FormikProps, useFormikContext } from 'formik';
import { CommonDataContext } from '../../context';
import _ from 'lodash';

export const CustomCheckbox = ({
  itemData,
  value,
  locale,
  displayLabel,
}: {
  itemData: FieldType;
  value: any;
  locale: string;
    displayLabel: string;
}) => {
  const { config: fieldConfig } = itemData;
  const [isInvalid, setIsInvalid] = useState(false);
  const formik: FormikProps<any> = useFormikContext();
  const { isMobileView, highlightErrorFieldsState } = useContext(CommonDataContext);

  const evaluateIfInvalid = (value: string): boolean => {
    return !!fieldConfig.required && _.isEmpty(value);
  };

  const handleChange = useCallback((value) => {
    setFieldValue(value);
  },
    [formik]
  );

  const setFieldValue = (valueToSet: string) => {
    formik.setFieldValue(itemData.config.fieldName, valueToSet, false);
    setIsInvalid(evaluateIfInvalid(valueToSet));
  };

  const _resetField = (): void => {
    // Check if error is already set to ''
    if (Boolean(_.get(formik.errors, itemData.config.fieldName))) {
      //Remove Error
      formik.setFieldError(itemData.config.fieldName, '');
    }

    if (!_.has(formik.values, itemData.config.fieldName)) { // do not set '' value for untouched fields
      return;
    }

    //Erase value
    formik.setFieldValue(itemData.config.fieldName, '');
  };

  useEffect(() => {
    if (value) {
      setIsInvalid(evaluateIfInvalid(value));
    }
    return () => {
      formik.setFieldError(itemData.config.fieldName, '');
    };
  }, []);

  useEffect(() => {
    //When there is no need to display the field, we reset it's state to a clean one
    if (!itemData.config.displayed) {
      _resetField();
    } else {
      // Checkbox is rendered but was never toggled by the user, so formik has no boolean value yet
      if (_.isUndefined(formik.values[itemData.config.fieldName])) {
        formik.setFieldValue(itemData.config.fieldName, false, false);
      }

      let isEmpty = false;
      if (itemData.config.required) {
        isEmpty = _.isEmpty(value);
      }
      formik.setFieldError(
        itemData.config.fieldName,
        isInvalid || isEmpty ? 'required' : ''
      );
    }
  }, [itemData.config.displayed, itemData.config.required, isInvalid, value]);

  useEffect(() => {
    if (highlightErrorFieldsState.state) {
      setIsInvalid(!!formik.getFieldMeta(itemData.config.fieldName).error);
    }
  }, [highlightErrorFieldsState]);

  return (
    <Box
      component={Grid}
      item
      xl={_.get(fieldConfig, 'columns.xl', 12)}
      lg={_.get(fieldConfig, 'columns.lg', 12)}
      md={_.get(fieldConfig, 'columns.md', 12)}
      sm={_.get(fieldConfig, 'columns.sm', 12)}
      xs={_.get(fieldConfig, 'columns.xs', 12)}
      display={{
        xl: !itemData.config.displayed ? 'none' : 'block',
        lg: !itemData.config.displayed ? 'none' : 'block',
        md: !itemData.config.displayed ? 'none' : 'block',
        sm: !itemData.config.displayed ? 'none' : 'block',
        xs: !itemData.config.displayed ? 'none' : 'block',
      }}
    >
      <FormControlLabel
        label={<span dangerouslySetInnerHTML={{ __html: displayLabel }} />}
        style={{ color: isInvalid ? '#d32f2f' : 'inherit' }}
        control={
          <Checkbox
            id={fieldConfig.fieldName}
            name={fieldConfig.fieldName}
            required={fieldConfig.required}
            checked={formik.values[fieldConfig.fieldName]}
            onChange={(_, checked) => {
              handleChange(checked);
            }}
          />
        }
      />
    </Box>
  );
};
